<script setup lang="ts">
import { computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { ChevronLeft, ChevronRight } from '@lucide/vue'

import { RepositoryTimeline, TimelineDraft } from '@types'

import { getSelectedLabel, periodTypeLabel } from './utils/helpers'
import { useResponsive } from '@/plugins/responsiveUI'

import { DSButton } from '@ds'
import PeriodScrollerAxis from './PeriodScrollerAxis.vue'
import PeriodScrollerTrigger from './PeriodScrollerTrigger.vue'

type Props = {
  timeline: RepositoryTimeline
  /** Available period-end dates, oldest first — from `analyticsStore.getDatesRange`. */
  dates: string[]
  /** Currently scrubbed date — from `analyticsStore.getDate`. */
  selected?: string
  /** Settings-only bar, no axis/steppers — for views that show every period at once. */
  compact?: boolean
  disabled?: boolean
}

type Emits = {
  'update:timeline': [timeline: RepositoryTimeline, isEndChanged?: boolean]
  'update:selected': [date: string]
}

const { timeline, dates, selected, compact = false, disabled = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScroller',
})

const { isMobile } = useResponsive()
const useSheet = computed(() => isMobile.value || Capacitor.isNativePlatform())

const typeLabel = computed(() => periodTypeLabel(timeline.freq))

const selectedIndex = computed(() => dates.indexOf(selected ?? ''))
const atStart = computed(() => selectedIndex.value <= 0)
const atEnd = computed(() => selectedIndex.value === -1 || selectedIndex.value >= dates.length - 1)

const selectedLabel = computed(() => getSelectedLabel(selected ?? '', timeline.freq, selectedIndex.value === dates.length - 1))

const select = (date: string) => {
  emit('update:selected', date)
}

const step = (direction: 1 | -1) => {
  const nextIndex = Math.min(dates.length - 1, Math.max(0, selectedIndex.value + direction))
  const next = dates[nextIndex]
  if (next) select(next)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (disabled) return
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    step(-1)
  } else if (event.key === 'ArrowRight') {
    event.preventDefault()
    step(1)
  }
}

const handleApply = (draft: TimelineDraft) => {
  const isEndChanged =
    draft.end !== timeline.end || draft.end_is_today !== timeline.end_is_today
  emit('update:timeline', { ...timeline, ...draft }, isEndChanged)
}
</script>

<template>
  <div
    class="ds-period-scroller font-ds w-full overflow-hidden border rounded-ds-lg border-border"
    @keydown="handleKeydown"
  >
    <div class="flex items-center gap-2 sm:gap-3.5 bg-background dark:bg-body-dark p-2.5 sm:px-4 sm:py-1">
      <!--
        Prev/next stepping is mobile-only: the desktop axis draws every period
        at once, so any period is a direct click away. Mobile keeps the
        steppers because the dense axis has no month labels to click precisely.
      -->
      <DSButton
        v-if="!compact"
        type="button"
        variant="secondary"
        size="icon"
        class="sm:hidden shrink-0 text-muted-foreground hover:bg-card hover:text-brand"
        :disabled="disabled || atStart"
        aria-label="Previous period"
        @click="step(-1)"
      >
        <ChevronLeft />
      </DSButton>

      <div class="sm:hidden flex flex-1 min-w-0 flex-col items-center gap-0.5">
        <span class="text-[0.625rem] font-bold tracking-ds-caps uppercase text-muted-foreground whitespace-nowrap">
          {{ typeLabel }} · Showing
        </span>
        <span class="text-[0.8125rem] font-bold text-foreground tabular-nums whitespace-nowrap">
          {{ selectedLabel }}
        </span>
      </div>

      <template v-if="!compact">
        <PeriodScrollerAxis
          class="hidden sm:block flex-1 min-w-0"
          :dates
          :freq="timeline.freq"
          :selected
          :disabled
          @select="select"
        />

        <DSButton
          type="button"
          variant="secondary"
          size="icon"
          class="sm:hidden shrink-0 text-muted-foreground hover:bg-card hover:text-brand"
          :disabled="disabled || atEnd"
          aria-label="Next period"
          @click="step(1)"
        >
          <ChevronRight />
        </DSButton>
        <div class="sm:hidden shrink-0 w-px h-6.5 bg-border" />
      </template>

      <PeriodScrollerTrigger
        :timeline
        :use-sheet="useSheet"
        :disabled
        @apply="handleApply"
      />
    </div>
    <div
      v-if="!compact"
      class="sm:hidden border-t border-border dark:bg-background px-4 pt-2.5 pb-1"
    >
      <PeriodScrollerAxis
        dense
        :dates
        :freq="timeline.freq"
        :selected
        :disabled
        @select="select"
      />
    </div>
  </div>
</template>
